//* claim line item
import { z } from 'zod';
import { DbJsonSchema } from '../../dbJsonSchemas.js';
import { CreditResolutionMethod, CrewClaimReasonCategoryCode, CrewClaimResolutionMethodEnum, CrewClaimStatusCode, CrewClaimStatusDetailCode, } from '../../enums/crew.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { exchangeRecommendation } from '../customer/schema/variantExchange.js';
import { claimReason } from './claimReasons.js';
import { appliedCustomField, appliedCustomFieldCreate } from './customField.js';
import { storeInspectionGrade, storeInspectionGradeCreate, } from './inspection.js';
import { storeOrderLineItem } from './lineItem.js';
import { Pegasus } from './pegasus.js';
import { storeProductVariant } from './storeProduct.js';
const claimLineItemFee = z.object({
    id: z.number(),
    displayName: z.string(),
    amount: z.number().nonnegative(),
    storeRuleId: z.number().optional(),
    storeRuleName: z.string().optional(),
});
export const claimLineItemFeeUpdate = z.object({
    amount: z.number().nonnegative(),
});
// ----------  Claim Line Item Inspection  ----------
export const inspectionImage = z.object({
    id: z.number(),
    url: z.string(),
});
export const inspectionImageCreate = inspectionImage
    .omit({
    id: true,
    url: true,
})
    .merge(z.object({ filename: z.string() }));
export const claimLineItemInspection = z.object({
    id: z.number(),
    createdOn: z.string(),
    name: z.string(),
    comment: helperSchema.nullifiedString.optional(),
    images: z.array(inspectionImage),
    customFields: z.array(appliedCustomField),
    storeInspectionGrade: storeInspectionGrade.optional(),
});
export const claimLineItemInspectionCreate = claimLineItemInspection
    .omit({
    id: true,
    images: true,
    storeInspectionGrade: true,
    customFields: true,
    createdOn: true,
})
    .merge(z.object({
    storeInspectionGrade: storeInspectionGradeCreate.optional(),
    images: z.array(inspectionImageCreate).optional(),
    customFields: z.array(z.object({ id: z.number(), value: z.string() })),
}));
// ----------  Claim Line Item  ----------
export const { variantExchangeLineItemSchema: variantExchangeLineItem } = DbJsonSchema.ClaimLineItem;
const variantExchangeLineItemCreate = variantExchangeLineItem
    .pick({
    idFromPlatform: true,
    quantity: true,
})
    // TODO: resolve why we can't use a default for any types that the customer-ui relies on... we have a lot of cases of this
    // duplicated logic
    .extend({
    price: z.coerce.number().default(0),
});
export const claimLineItem = z.object({
    id: z.number(),
    finalizedOn: z.string().nullable(),
    canceledOn: z.string().optional(),
    wasOutsideEligibilityWindow: z.boolean().optional(),
    deniedOn: z.string().optional(),
    returnLineItemIdFromPlatform: z.string().nullable(),
    wasResolvedExternally: z.boolean().optional(),
    suggestedWarrantyReplacementItem: storeProductVariant.optional(),
    // ! all line item claims
    noteToCustomer: z.string().optional(),
    errors: z.array(z.string()).optional(),
    fees: z.array(claimLineItemFee).optional(),
    requestedResolutionMethodEnum: z.nativeEnum(CrewClaimResolutionMethodEnum),
    resolutionMethodEnum: z.nativeEnum(CrewClaimResolutionMethodEnum).optional(),
    inspections: z.array(claimLineItemInspection),
    /**
     * @deprecated use media
     */
    images: z.array(z.string()).optional(),
    media: z
        .array(z.object({
        url: z.string(),
        // id on relationship
        claimLineItemImageId: z.number(),
        filename: z.string(),
    }))
        .optional(),
    comments: z.string().nullable(), // ? noteFromCustomer
    originalStoreOrderLineItemId: z.number(),
    originalStoreOrderLineItem: storeOrderLineItem,
    deductedAmount: z.number().optional(),
    exchangeDeltaAmount: z.number().optional(),
    isShopNow: z.boolean().optional(), // * is this a shop now claim
    outstandingAmountFinalizedOn: z.string().optional(),
    exchangeRecommendation: exchangeRecommendation.optional(),
    wasExchangeRecommendationAccepted: z.boolean().optional(),
    variantExchangeAmountResolutionMethodEnum: z
        .nativeEnum(CreditResolutionMethod)
        .optional(),
    status: z.object({
        id: z.number(),
        name: z.string(),
        code: enumToZodLiteralUnion(CrewClaimStatusCode),
        detail: z.object({
            id: z.number(),
            name: z.string(),
            code: enumToZodLiteralUnion(CrewClaimStatusDetailCode),
        }),
    }),
    customFields: z.array(appliedCustomField),
    reason: z.object({
        id: z.number(),
        name: z.string(),
        category: claimReason.shape.category,
        detail: z
            .object({
            id: z.number(),
            name: z.string(),
        })
            .optional(), // ! detail will always remain optional on the reason as it's not required
    }),
    updatedOn: z.string().optional(),
    createdOn: z.string(),
    // ! Variant Exchange Line Item Claims
    variantExchangeLineItem: variantExchangeLineItem.optional(),
    // resolved from the catalog at finalization; the exchange snapshot itself has no product id
    exchangeToProductIdFromPlatform: z.string().optional(),
    replacementItems: z.array(variantExchangeLineItem).optional(),
    // ! Monetary Claims (Refund, Gift Card)
    quantity: z.number(),
    unitPrice: z.number(),
    unitTax: z.number(),
    amount: z.number().optional(), // issued monetary value of the claim line item applies to refunds and gift cards
    requestedAmount: z.number().optional(), // requested monetary value of the claim line item applies to refunds and gift cards
});
export const claimLineItemCreate = claimLineItem
    .pick({
    comments: true,
    originalStoreOrderLineItemId: true,
    quantity: true,
    unitPrice: true,
    unitTax: true,
    requestedResolutionMethodEnum: true,
    images: true,
    exchangeDeltaAmount: true,
    variantExchangeAmountResolutionMethodEnum: true,
    replacementItems: true,
    exchangeRecommendation: true,
    wasExchangeRecommendationAccepted: true,
    wasOutsideEligibilityWindow: true,
})
    .merge(z.object({
    variantExchangeLineItem: variantExchangeLineItem
        .pick({
        idFromPlatform: true,
        quantity: true,
        price: true,
        imgUrl: true,
        options: true,
        title: true,
    })
        .optional(),
    customFields: z.array(appliedCustomFieldCreate).optional(),
    reasonId: z.number(),
    reasonDetailId: z.number().optional(),
    reasonCategoryCode: enumToZodLiteralUnion(CrewClaimReasonCategoryCode),
    advancedExchangeMethod: Pegasus.advancedExchangeMethodUnion.optional(),
    fees: z
        .array(claimLineItemFee.omit({
        id: true,
    }))
        .optional(),
}));
export const calculatedOrderLineItem = claimLineItemCreate.extend({
    id: z.string(),
    grams: z.coerce.number(),
    originalStoreOrderLineItem: storeOrderLineItem
        .pick({
        name: true,
        optionsFromPlatform: true,
        imgUrl: true,
    })
        .extend({
        idFromPlatform: z.string().nullable(),
    }),
});
export const calculatedOrderLineItemResponse = calculatedOrderLineItem
    .pick({
    id: true,
    quantity: true,
    unitPrice: true,
    unitTax: true,
    requestedResolutionMethodEnum: true,
})
    .merge(storeOrderLineItem.pick({
    name: true,
    imgUrl: true,
    optionsFromPlatform: true,
}));
export const calculatedLineItemApplication = calculatedOrderLineItemResponse
    .pick({
    id: true,
})
    .merge(claimLineItemCreate.pick({
    variantExchangeAmountResolutionMethodEnum: true,
    exchangeDeltaAmount: true,
}))
    .merge(z.object({
    requestedResolutionMethodEnum: z.enum([
        CrewClaimResolutionMethodEnum.variantExchange,
        CrewClaimResolutionMethodEnum.giftCard,
        CrewClaimResolutionMethodEnum.refund,
        CrewClaimResolutionMethodEnum.other,
        CrewClaimResolutionMethodEnum.warrantyReview,
        CrewClaimResolutionMethodEnum.replacementOrder,
    ]),
}));
const resolvedExternallyUpdate = z.object({
    wasResolvedExternally: z.boolean().default(false),
});
const exchangeUpdate = z.object({
    variantExchangeLineItem: variantExchangeLineItemCreate,
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.variantExchange}`),
});
const repairUpdate = z.object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.repair}`),
});
const monetaryUpdate = z.object({ amount: z.number() });
const refundUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.refund}`),
})
    .merge(monetaryUpdate);
const giftCardUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.giftCard}`),
})
    .merge(monetaryUpdate);
const replacementOrderUpdate = z
    .object({
    resolutionMethod: z.literal(`${CrewClaimResolutionMethodEnum.replacementOrder}`),
})
    .merge(DbJsonSchema.ClaimLineItem.replacementOrderLineItems);
const claimLineItemUpdateApproved = z.discriminatedUnion('resolutionMethod', [
    exchangeUpdate.merge(resolvedExternallyUpdate),
    refundUpdate.merge(resolvedExternallyUpdate),
    giftCardUpdate.merge(resolvedExternallyUpdate),
    replacementOrderUpdate.merge(resolvedExternallyUpdate),
    repairUpdate,
]);
const claimLineItemUpdateDeny = z.object({
    // ? these values come from the claim status helper... should we use the enum here?
    claimStatusDetailCode: z.union([
        z.literal(`${CrewClaimStatusDetailCode.wrongItem}`),
        z.literal(`${CrewClaimStatusDetailCode.itemNotReturned}`),
        z.literal(`${CrewClaimStatusDetailCode.noncompliantReturn}`),
        z.literal(`${CrewClaimStatusDetailCode.notCoveredUnderWarranty}`),
        z.literal(`${CrewClaimStatusDetailCode.noActionTaken}`),
    ]),
});
const claimLineItemUpdateCancel = z.object({
    // ? these values come from the claim status helper... should we use the enum here?
    claimStatusDetailCode: z.union([
        z.literal(`${CrewClaimStatusDetailCode.noActionTaken}`),
        z.literal(`${CrewClaimStatusDetailCode.itemNotReturned}`),
    ]),
});
export const claimLineItemUpdateResolution = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('reason'),
        reason: z.object({
            id: z.number(),
            detailId: z.number().nullable(),
        }),
    }),
    z.object({
        kind: z.literal('note'),
        noteToCustomer: helperSchema.nullifiedString,
    }),
    z.object({
        kind: z.literal('approved'),
        approval: claimLineItemUpdateApproved,
    }),
    z.object({ kind: z.literal('denied'), denial: claimLineItemUpdateDeny }),
    z.object({
        // american english spelling around cancel uses one l as a verb, 'canceled', and but two l's as a noun, 'cancellation'
        kind: z.literal('canceled'),
        cancellation: claimLineItemUpdateCancel,
    }),
]);
// -------------- Claim Finalization Line Item --------------
const baseClaimLineItemProcess = claimLineItem
    .pick({
    id: true,
    noteToCustomer: true,
    errors: true,
    quantity: true,
    returnLineItemIdFromPlatform: true,
    deductedAmount: true,
})
    .extend({
    wasZeroedThroughDeduction: z.boolean().optional(),
    isPartialExchangeCredit: z.boolean().optional(),
});
// ----------  Claim Line Item Deny  ----------
export const denyClaimLineItem = baseClaimLineItemProcess.merge(z.object({
    claimStatusDetailCode: z.string(), // ! note to future us, we should make this use the enum values someday
}));
// ----------  Claim Line Item Approve Variant Exchange ----------
const approvedVariantExchange = baseClaimLineItemProcess
    .merge(z.object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.variantExchange}`),
}))
    .merge(claimLineItem
    .pick({
    exchangeDeltaAmount: true,
    variantExchangeLineItem: true,
})
    .required());
// ----------  Claim Line Item Approve Replacement Order ----------
const approvedReplacementOrder = baseClaimLineItemProcess.merge(z
    .object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.replacementOrder}`),
    replacementItems: z.array(variantExchangeLineItem),
})
    .merge(claimLineItem
    .pick({
    isShopNow: true,
    exchangeDeltaAmount: true,
})
    .required()));
// ----------  Claim Line Item Approve Monetary ----------
const baseApprovedMonetary = baseClaimLineItemProcess
    .merge(z.object({
    amount: z.number(),
    requestedAmount: z.number(),
}))
    .merge(baseClaimLineItemProcess)
    .required({
    deductedAmount: true,
});
const approvedGiftCard = baseApprovedMonetary.merge(z.object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.giftCard}`),
}));
const approvedRefund = baseApprovedMonetary.merge(z.object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.refund}`),
}));
const approvedOther = baseApprovedMonetary.merge(z.object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.other}`),
}));
const approvedRepair = baseClaimLineItemProcess.merge(z.object({
    resolutionMethodEnum: z.literal(`${CrewClaimResolutionMethodEnum.repair}`),
}));
export const approvedFinalizationLineItem = z.discriminatedUnion('resolutionMethodEnum', [
    approvedRefund,
    approvedGiftCard,
    approvedVariantExchange,
    approvedReplacementOrder,
    approvedOther,
    approvedRepair,
]);
const approved = z.object({
    refunds: z.array(approvedRefund),
    giftCards: z.array(approvedGiftCard),
    variantExchanges: z.array(approvedVariantExchange),
    replacementOrders: z.array(approvedReplacementOrder),
    other: z.array(approvedOther),
    repairs: z.array(approvedRepair),
});
export const finalizationLineItemPayload = z.object({
    claimLineItems: z.object({
        approved,
        approvedWithExternalResolution: approved
            .pick({
            refunds: true,
            giftCards: true,
            variantExchanges: true,
            replacementOrders: true,
            other: true,
        })
            .optional(),
        denied: z.array(denyClaimLineItem),
    }),
});
//# sourceMappingURL=claimLineItem.js.map